import { useEventListener } from 'ahooks';
import { useCustomUpload, useCustomVideoUpload } from 'hooks/business/useUploadResource';
import { RefObject } from 'react';
import { isAuthStatusValid } from 'utils/is';
import validatorVideo from 'utils/validator/video';

interface UseNeuronUploadProps {
  neuronId: string;
  storeList?: Store.Store[];
  storeGroupId?: string;
  iframeRef: RefObject<HTMLIFrameElement>;
}

function useNeuronUpload(props: UseNeuronUploadProps) {
  const { neuronId, storeList, storeGroupId, iframeRef } = props;

  const customUpload = useCustomUpload();

  const customVideoUpload = useCustomVideoUpload();

  useEventListener(
    'message',
    function handler(e: MessageEvent) {
      if (!e.data || e.data.neuronId !== neuronId) {
        return;
      }

      let storeId;
      let region;

      const platform = e.data.platform ? e.data.platform.toUpperCase() : undefined;

      if (platform) {
        const store = (storeList || []).find((_) => {
          return (
            _.externalType === platform &&
            isAuthStatusValid(_.erpAuthStatus) &&
            isAuthStatusValid(_.chatAuthStatus)
          );
        });

        if (!store) {
          return;
        }

        storeId = `${store.id}`;
        region = store.region;
      }

      if (!iframeRef.current || !iframeRef.current.contentWindow) {
        return;
      }

      if (e.data.type === 'uploadImage') {
        const {
          current: { contentWindow: neuron },
        } = iframeRef;

        const {
          data: { file, id },
        } = e;

        customUpload(
          {
            file,
            onSuccess: (data) => {
              neuron.postMessage(
                {
                  type: 'uploadImageSuccess',
                  data,
                  id,
                  neuronId,
                },
                '*',
              );
            },
            onError: (data) => {
              neuron.postMessage(
                {
                  type: 'uploadImageError',
                  data,
                  id,
                  neuronId,
                },
                '*',
              );
            },
            onProgress: (data) => {
              neuron.postMessage(
                {
                  type: 'uploadImageProgress',
                  data,
                  id,
                  neuronId,
                },
                '*',
              );
            },
            action: '',
            method: 'post',
          },
          {
            storeId,
            fileType: 'IMAGE',
            reqSource: 'qa',
            uploadResourceCentre: !platform,
            storeGroupId,
          },
        );
      }

      if (e.data.type === 'uploadVideo') {
        const {
          current: { contentWindow: neuron },
        } = iframeRef;

        const {
          data: { file, id },
        } = e;

        customVideoUpload(
          {
            file,
            onSuccess: (data) => {
              neuron.postMessage(
                {
                  type: 'uploadVideoSuccess',
                  data,
                  id,
                  neuronId,
                },
                '*',
              );
            },
            onError: (data) => {
              neuron.postMessage(
                {
                  type: 'uploadVideoError',
                  data,
                  id,
                  neuronId,
                },
                '*',
              );
            },
            onProgress: (data) => {
              neuron.postMessage(
                {
                  type: 'uploadVideoProgress',
                  data,
                  id,
                  neuronId,
                },
                '*',
              );
            },
            action: '',
            method: 'post',
          },
          storeId,
          region,
          !platform,
          storeGroupId,
        );
      }

      if (e.data.type === 'validateVideo') {
        if (!iframeRef.current || !iframeRef.current.contentWindow) {
          return;
        }

        const {
          current: { contentWindow: neuron },
        } = iframeRef;

        const {
          data: { file, id },
        } = e;

        validatorVideo(platform?.toUpperCase(), file)
          .catch(() => ({ errorText: '' }))
          .then(({ errorText: text }) => {
            neuron.postMessage(
              {
                type: 'validateVideoResult',
                text,
                id,
                neuronId,
              },
              '*',
            );
          });
      }
    },
    {
      target: window,
    },
  );
}

export default useNeuronUpload;
